import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Eye, Shield, Heart, Unlock, ChevronRight } from 'lucide-react';
import axios from 'axios';
import PremiumLock from '@/components/PremiumLock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const phaseLabels = {
  understand: 'Förstå',
  handle: 'Hantera',
  accept: 'Acceptera',
  forgive: 'Förlåta'
};

const phaseIcons = {
  understand: Eye,
  handle: Shield,
  accept: Heart,
  forgive: Unlock
};

const phaseColors = {
  understand: 'bg-blue-100/50',
  handle: 'bg-amber-100/50',
  accept: 'bg-emerald-100/50',
  forgive: 'bg-violet-100/50'
};

const phaseDescriptions = {
  understand: 'Första steget är att se och förstå dina triggers',
  handle: 'Lär dig reglera dig själv när det händer',
  accept: 'Sluta kämpa mot det som var – frigör energi',
  forgive: 'Ge dig själv gåvan av frihet'
};

export default function TriggersPage() {
  const navigate = useNavigate();
  const { hasAccess } = useAuth();
  
  const [triggers, setTriggers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTrigger, setSelectedTrigger] = useState(null);

  useEffect(() => {
    if (hasAccess) {
      fetchTriggers();
    } else {
      setLoading(false);
    }
  }, [hasAccess]);

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
            <Button variant="ghost" onClick={() => navigate('/category/thoughts')} className="rounded-full hover:bg-secondary/80">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Tillbaka
            </Button>
            <h1 className="font-heading text-xl font-light">Triggers</h1>
            <div className="w-24" />
          </div>
        </header>
        <PremiumLock feature="Trigger-verktyg" />
      </div>
    );
  }

  const fetchTriggers = async () => {
    try {
      const response = await axios.get(`${API}/triggers`);
      setTriggers(response.data);
    } catch (error) {
      toast.error('Kunde inte hämta trigger-verktyg');
    } finally {
      setLoading(false);
    }
  };

  const phases = ['understand', 'handle', 'accept', 'forgive'];

  return (
    <div className="min-h-screen bg-background" data-testid="triggers-page">
      {/* Header */}
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
          <Button 
            variant="ghost" 
            onClick={() => selectedTrigger ? setSelectedTrigger(null) : navigate('/category/thoughts')}
            className="rounded-full hover:bg-secondary/80"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {selectedTrigger ? 'Tillbaka' : 'Startsida'}
          </Button>
          <h1 className="font-heading text-xl font-light">Triggers</h1>
          <div className="w-24" />
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Trigger detail view */}
        {selectedTrigger ? (
          <div className="animate-fade-in space-y-8">
            {/* Header */}
            <div className={`${phaseColors[selectedTrigger.phase]} rounded-2xl p-8 lg:p-12`}>
              {(() => {
                const Icon = phaseIcons[selectedTrigger.phase];
                return (
                  <div className="w-16 h-16 rounded-full bg-card flex items-center justify-center mb-6">
                    <Icon className="w-7 h-7 text-primary" />
                  </div>
                );
              })()}
              <span className="text-sm px-3 py-1 rounded-full bg-card/80 inline-block mb-4">
                Steg {phases.indexOf(selectedTrigger.phase) + 1} av 4
              </span>
              <h2 className="font-heading text-3xl font-light mb-4">{selectedTrigger.title}</h2>
              <p className="text-muted-foreground leading-relaxed text-lg">{selectedTrigger.description}</p>
            </div>

            {/* Questions */}
            <div className="bg-card rounded-2xl p-8 space-y-4">
              <h3 className="font-heading text-2xl">Frågor att utforska</h3>
              <div className="space-y-3">
                {selectedTrigger.questions.map((question, index) => (
                  <div key={index} className="flex gap-4 items-start p-4 rounded-xl bg-secondary/30">
                    <span className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium text-primary shrink-0">
                      {index + 1}
                    </span>
                    <span className="text-foreground">{question}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Exercises */}
            <div className="bg-secondary/30 rounded-2xl p-8 space-y-4">
              <h3 className="font-heading text-2xl">Övningar</h3>
              <ul className="space-y-3">
                {selectedTrigger.exercises.map((exercise, index) => (
                  <li key={index} className="flex gap-3 items-start">
                    <div className="w-2 h-2 rounded-full bg-primary mt-2 shrink-0" />
                    <span className="text-muted-foreground">{exercise}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Affirmations */}
            <div className="bg-primary/5 rounded-2xl p-8 space-y-4">
              <h3 className="font-heading text-2xl">Affirmationer</h3>
              <div className="space-y-3">
                {selectedTrigger.affirmations.map((affirmation, index) => (
                  <p key={index} className="text-lg font-light text-center py-3 border-b border-border/30 last:border-0">
                    "{affirmation}"
                  </p>
                ))}
              </div>
            </div>

            {/* Navigate to next phase */}
            {phases.indexOf(selectedTrigger.phase) < phases.length - 1 && (
              <div className="flex justify-center pt-4">
                <Button
                  onClick={() => {
                    const nextPhase = phases[phases.indexOf(selectedTrigger.phase) + 1];
                    const nextTrigger = triggers.find(t => t.phase === nextPhase);
                    if (nextTrigger) setSelectedTrigger(nextTrigger);
                  }}
                  className="rounded-full px-8"
                  data-testid="next-phase-btn"
                >
                  Nästa steg: {phaseLabels[phases[phases.indexOf(selectedTrigger.phase) + 1]]}
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Intro */}
            <div className="text-center mb-12 space-y-4 animate-fade-in">
              <h2 className="font-heading text-3xl font-light">Bearbeta dina triggers</h2>
              <p className="text-muted-foreground max-w-lg mx-auto">
                En fyra-stegs resa för att förstå, hantera, acceptera och förlåta 
                det svåra i livet.
              </p>
            </div>

            {/* Journey visualization */}
            <div className="relative mb-12">
              <div className="absolute top-1/2 left-0 right-0 h-1 bg-border -translate-y-1/2 hidden md:block" />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 relative">
                {phases.map((phase, index) => {
                  const Icon = phaseIcons[phase];
                  return (
                    <div key={phase} className="text-center">
                      <div className={`w-16 h-16 mx-auto rounded-full ${phaseColors[phase]} flex items-center justify-center mb-3 relative z-10`}>
                        <Icon className="w-7 h-7 text-primary" />
                      </div>
                      <span className="text-xs text-muted-foreground">Steg {index + 1}</span>
                      <h3 className="font-heading text-lg">{phaseLabels[phase]}</h3>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Trigger cards */}
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary/50" />
              </div>
            ) : (
              <div className="space-y-4">
                {triggers.map((trigger, index) => {
                  const Icon = phaseIcons[trigger.phase];
                  return (
                    <button
                      key={trigger.id}
                      onClick={() => setSelectedTrigger(trigger)}
                      className={`w-full ${phaseColors[trigger.phase]} p-6 rounded-2xl text-left hover:shadow-soft transition-shadow duration-300 opacity-0 animate-slide-up group flex items-center justify-between`}
                      style={{ animationDelay: `${index * 0.1}s` }}
                      data-testid={`trigger-card-${trigger.phase}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-card flex items-center justify-center group-hover:-translate-y-1 transition-transform duration-300">
                          <Icon className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <span className="text-xs text-muted-foreground">Steg {index + 1}</span>
                          <h3 className="font-heading text-xl font-normal">{trigger.title}</h3>
                          <p className="text-sm text-muted-foreground mt-1">{phaseDescriptions[trigger.phase]}</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:translate-x-1 transition-transform duration-300" />
                    </button>
                  );
                })}
              </div>
            )}

            {/* Reminder */}
            <div className="mt-12 text-center">
              <p className="text-sm text-muted-foreground font-light italic">
                "Att bearbeta triggers är en resa, inte en destination. Ta den tid du behöver."
              </p>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
